/**
 * PlaybookEngine - Core automation execution engine
 * 
 * This module is the heart of the Playbooks system. It:
 * - Matches events against user-created playbook triggers
 * - Executes configured actions (createTask, sendSMS)
 * - Respects enable/disable state
 * - Is designed to be framework-agnostic for future backend use
 * 
 * IMPORTANT: Nothing runs unless:
 * 1. A user explicitly created the Playbook
 * 2. The Playbook is enabled (active: true)
 * 
 * If no playbooks exist for an event, nothing happens.
 */

import axios from 'axios';
import { auth } from '../firebase';

/**
 * Supported trigger types
 */
export const TRIGGER_TYPES = {
  LEAD_CREATED: 'leadCreated',
  STAGE_CHANGED: 'stageChanged',
};

/**
 * Supported action types
 */
export const ACTION_TYPES = {
  CREATE_TASK: 'createTask',
  SEND_SMS: 'sendSMS',
};

/**
 * Check if a playbook's trigger matches the given event
 * 
 * @param {Object} trigger - The playbook's trigger configuration
 * @param {string} eventType - The type of event that occurred
 * @param {Object} payload - Event-specific data
 * @returns {boolean} - Whether the trigger matches
 */
export function matchesTrigger(trigger, eventType, payload) {
  if (!trigger || !trigger.type) return false;

  // leadCreated - simple type match
  if (eventType === TRIGGER_TYPES.LEAD_CREATED && trigger.type === TRIGGER_TYPES.LEAD_CREATED) {
    return true;
  }

  // stageChanged - supports from/to filtering
  if (eventType === TRIGGER_TYPES.STAGE_CHANGED && trigger.type === TRIGGER_TYPES.STAGE_CHANGED) {
    const { from, to } = payload;
    // null/undefined/empty string means "any"
    const fromMatch = !trigger.from || trigger.from === '' || trigger.from === from;
    const toMatch = !trigger.to || trigger.to === '' || trigger.to === to;
    return fromMatch && toMatch;
  }

  return false;
}

/**
 * Calculate due date based on offset configuration
 * 
 * @param {number} offsetDays - Days from now
 * @returns {string} - Date in YYYY-MM-DD format
 */
function calculateDueDate(offsetDays = 0) {
  const today = new Date();
  const dueDateObj = new Date(today);
  dueDateObj.setDate(dueDateObj.getDate() + offsetDays);

  const yyyy = dueDateObj.getFullYear();
  const mm = String(dueDateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dueDateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Calculate execution time based on delay configuration
 * 
 * @param {Object} delay - Delay configuration { value, unit }
 * @returns {Date} - When to execute the action
 */
function calculateExecutionTime(delay) {
  const now = new Date();
  if (!delay || !delay.value || delay.value === 0) {
    return now;
  }

  const delayMs = delay.unit === 'hours' 
    ? delay.value * 60 * 60 * 1000
    : delay.value * 24 * 60 * 60 * 1000; // days

  return new Date(now.getTime() + delayMs);
}

/**
 * Interpolate merge tags in a message template
 * 
 * @param {string} template - Message with {{tags}}
 * @param {Object} data - Data to interpolate
 * @returns {string} - Interpolated message
 */
export function interpolateMessage(template, data) {
  if (!template) return '';
  
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    // Handle nested keys like lead.name
    const value = data[key];
    if (value !== undefined && value !== null) {
      return String(value);
    }
    // Return empty string for missing values (don't leave {{tag}})
    return '';
  });
}

/**
 * Execute a createTask action
 * 
 * @param {Object} action - The action configuration
 * @param {Object} payload - Event payload containing leadId
 * @param {Function} addTask - Task creation function from TasksContext
 */
async function executeCreateTask(action, payload, { addTask }) {
  if (!action.task || !addTask) {
    console.warn('[PlaybookEngine] createTask action missing task config or addTask function');
    return;
  }

  const { task } = action;
  const leadId = payload.leadId || payload.lead?.id;
  
  if (!leadId) {
    console.warn('[PlaybookEngine] createTask action missing leadId');
    return;
  }

  const offsetDays = typeof task.dueOffsetDays === 'number' ? task.dueOffsetDays : 0;
  const dueDate = calculateDueDate(offsetDays);
  const dueTime = task.dueOffsetTime || null;

  await addTask({
    leadId,
    title: task.title,
    description: task.description || '',
    priority: task.priority || 'medium',
    taskType: task.taskType || 'other',
    type: 'task',
    dueDate,
    dueTime,
  });

  console.log('[PlaybookEngine] Created task:', task.title);
}

/**
 * Execute a sendSMS action
 * 
 * @param {Object} action - The action configuration
 * @param {Object} payload - Event payload containing lead data
 * @param {Object} context - Execution context with companyId
 */
async function executeSendSMS(action, payload, context) {
  if (!action.sms) {
    console.warn('[PlaybookEngine] sendSMS action missing sms config');
    return;
  }

  const { sms } = action;
  const lead = payload.lead || {};
  const phone = lead.phone || lead.clientPhone;

  if (!phone) {
    console.warn('[PlaybookEngine] sendSMS action: lead has no phone number');
    return;
  }

  // Build interpolation data from lead
  const interpolationData = {
    first_name: lead.firstName || lead.name?.split(' ')[0] || '',
    last_name: lead.lastName || lead.name?.split(' ').slice(1).join(' ') || '',
    name: lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
    company: lead.company || '',
    address: lead.address?.line1 || lead.address?.streetName || '',
    city: lead.address?.city || '',
    state: lead.address?.state || '',
  };

  const message = interpolateMessage(sms.message, interpolationData);

  if (!message.trim()) {
    console.warn('[PlaybookEngine] sendSMS action: message is empty after interpolation');
    return;
  }

  try {
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      console.error('[PlaybookEngine] sendSMS: No authenticated user');
      return;
    }

    const token = await currentUser.getIdToken(true);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    await axios.post(
      `${backendUrl}/api/sms/send`,
      {
        to: phone,
        message: message,
        // Note: No conversationId - this creates a new outbound SMS
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    console.log('[PlaybookEngine] Sent SMS to:', phone);
  } catch (error) {
    console.error('[PlaybookEngine] Failed to send SMS:', error);
    throw error;
  }
}

/**
 * Execute a playbook action
 * 
 * @param {Object} action - The action configuration
 * @param {Object} payload - Event payload
 * @param {Object} context - Execution context
 */
async function executeAction(action, payload, context) {
  if (!action || !action.type) {
    console.warn('[PlaybookEngine] Action missing type');
    return;
  }

  switch (action.type) {
    case ACTION_TYPES.CREATE_TASK:
      await executeCreateTask(action, payload, context);
      break;

    case ACTION_TYPES.SEND_SMS:
      await executeSendSMS(action, payload, context);
      break;

    default:
      console.warn('[PlaybookEngine] Unknown action type:', action.type);
  }
}

/**
 * Main engine entry point - run all matching playbooks for an event
 * 
 * @param {string} eventType - The type of event (leadCreated, stageChanged)
 * @param {Object} payload - Event-specific data
 * @param {Array} playbooks - List of user-created playbooks
 * @param {Object} context - Execution context { companyId, addTask, ... }
 */
export async function runPlaybooks(eventType, payload, playbooks, context) {
  // CRITICAL: If no playbooks exist, do nothing
  if (!playbooks || playbooks.length === 0) {
    console.log('[PlaybookEngine] No playbooks configured - nothing to run');
    return;
  }

  // Filter to only active playbooks with valid trigger and action
  const activePlaybooks = playbooks.filter(
    (pb) => pb.active && pb.trigger && pb.action
  );

  if (activePlaybooks.length === 0) {
    console.log('[PlaybookEngine] No active playbooks - nothing to run');
    return;
  }

  // Process each playbook
  for (const playbook of activePlaybooks) {
    const { trigger, action, delay } = playbook;

    // Check if trigger matches
    if (!matchesTrigger(trigger, eventType, payload)) {
      continue;
    }

    console.log(`[PlaybookEngine] Matched playbook: "${playbook.name}"`);

    // Check delay (for V1, we execute immediately or skip delayed ones)
    // Future: Implement scheduled execution
    if (delay && delay.value > 0) {
      console.log(`[PlaybookEngine] Playbook "${playbook.name}" has delay - immediate execution only in V1`);
      // For V1, we'll still execute immediately but log the intended delay
      // A proper implementation would schedule this
    }

    // Execute the action
    try {
      await executeAction(action, payload, context);
    } catch (error) {
      console.error(`[PlaybookEngine] Error executing playbook "${playbook.name}":`, error);
      // Continue with other playbooks even if one fails
    }
  }
}

/**
 * PlaybookEngine class for object-oriented usage
 */
class PlaybookEngine {
  /**
   * Run playbooks for an event
   * 
   * @param {string} eventType - Event type
   * @param {Object} payload - Event data
   * @param {Array} playbooks - User playbooks
   * @param {Object} context - Execution context
   */
  static async run(eventType, payload, playbooks, context) {
    return runPlaybooks(eventType, payload, playbooks, context);
  }

  /**
   * Check if a trigger matches an event (useful for previews)
   */
  static matchesTrigger(trigger, eventType, payload) {
    return matchesTrigger(trigger, eventType, payload);
  }

  /**
   * Interpolate a message template (useful for previews)
   */
  static interpolateMessage(template, data) {
    return interpolateMessage(template, data);
  }
}

export default PlaybookEngine;
